import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { paymentService } from "../../services/paymentService";
import { courseService } from "../../services/courseService";
import Logo from "../../asset/images/home/Logo (1).png";

function PaymentCard() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [course, setCourse] = useState(null);

    useEffect(() => {
        
        if (!isAuthenticated) {
            navigate("/login?returnUrl=/register/payment-card");
            return;
        }

        const canceled = searchParams.get("canceled");
        if (canceled === "true") {
            setError("Payment was canceled. You can try again when ready.");
        }

        loadCourse();
    }, [isAuthenticated, navigate, searchParams]);

    const loadCourse = async () => {
        try {
            setLoading(true);
            
            const coursesResult = await courseService.getCourses();
            if (!coursesResult.success || coursesResult.data.courses.length === 0) {
                setError("No courses available. Please contact support.");
                return;
            }

            const firstCourse = coursesResult.data.courses[0];
            setCourse(firstCourse);
        } catch (err) {
            setError("Failed to load course information. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleProceedToPayment = async () => {
        if (!course) {
            setError("Course information not loaded. Please refresh the page.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const checkoutResult = await paymentService.createCheckoutSession(
                course.id,
                'INDIVIDUAL'
            );

            if (checkoutResult.success && checkoutResult.data.url) {
                window.location.href = checkoutResult.data.url;
            } else {
                setError("Failed to initialize payment. Please try again.");
            }
        } catch (err) {
            setError("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading && !course) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading payment information...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null; 
    }

    return (
        <>
            {/* Header */}
            <header className="bg-white shadow-md">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex justify-between items-center">
                        {/* Logo */}
                        <div className="flex items-center">
                            <img src={Logo} alt="READ 21 Logo" className="h-16 w-auto" />
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center space-x-8">
                            <Link
                                to="/about"
                                className="bg-transparent text-gray-700 px-8 py-3 rounded-md font-medium text-lg hover:bg-primaryDark hover:text-white transition-colors"
                            >
                                About
                            </Link>

                            <Link to="/register">
                                <button className="bg-primary text-white px-8 py-3 rounded-md font-medium hover:bg-primaryDark transition-colors text-lg">
                                    Enroll Today
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Section */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="bg-white rounded-xl shadow-lg p-10">
                        {/* Page Title & Stepper */}
                        <div className="mb-12">
                            <h1 className="text-3xl font-bold text-primary text-center mb-10">
                                Complete Your Payment
                            </h1>

                            <div className="relative flex items-center justify-between max-w-2xl mx-auto">
                                {/* Connecting Line */}
                                <div className="absolute left-[7%] right-[7%] top-4 h-0.5 bg-gray-300 z-0"></div>

                                {/* Step 1 Complete */}
                                <div className="flex flex-col items-center relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-white border-2 border-primary flex items-center justify-center mb-2">
                                        <svg
                                            className="w-5 h-5 text-primary"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-primary">Registration</span>
                                </div>

                                {/* Step 2 Active */}
                                <div className="flex flex-col items-center relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center mb-2">
                                        <span className="text-sm font-bold">2</span>
                                    </div>
                                    <span className="text-sm text-primary font-semibold underline">Payment</span>
                                </div>

                                {/* Step 3 */}
                                <div className="flex flex-col items-center relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center mb-2"></div>
                                    <span className="text-sm text-gray-500">Complete</span>
                                </div>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Payment Summary */}
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-gray-50 rounded-xl p-8 mb-8">
                                <h2 className="text-xl font-semibold text-gray-800 mb-6">Order Summary</h2>
                                
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                                        <span className="text-gray-700">Course:</span>
                                        <span className="font-semibold text-gray-800">
                                            {course?.title || "Read 21's Fast Track to Reading"}
                                        </span>
                                    </div>
                                    
                                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                                        <span className="text-gray-700">Price:</span>
                                        <span className="font-semibold text-gray-800">
                                            ${course ? Number(course.individualPrice).toFixed(2) : "250.00"}
                                        </span>
                                    </div>

                                    <div className="flex justify-between items-center pt-4">
                                        <span className="text-lg font-semibold text-gray-800">Total:</span>
                                        <span className="text-2xl font-bold text-primary">
                                            ${course ? Number(course.individualPrice).toFixed(2) : "250.00"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleProceedToPayment}
                                disabled={loading || !course}
                                className="w-full rounded-lg bg-green hover:bg-greenDark text-white font-semibold py-4 px-6 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                            >
                                {loading ? "Processing..." : "Proceed to Payment"}
                            </button>

                            <p className="text-center text-sm text-gray-500 mt-4">
                                By proceeding, you agree to our terms and conditions
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-primary py-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <div className="mb-3">
                        <img
                            src={Logo}
                            alt="READ 21 Logo"
                            className="mx-auto h-16 w-auto"
                        />
                    </div>
                    <p className="text-white text-lg">Read 21 © 2025 - All Rights Reserved</p>
                </div>
            </footer>
        </>
    );
}

export default PaymentCard;
